import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import {
  Modal,
  Row,
  Col,
  Form,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCamera } from '@fortawesome/pro-regular-svg-icons';
import { SocketContext } from '../../../../../socketProvider';

const CandidPhotosModal = ({
  show,
  onHide,
  camerasStatus,
}) => {
  const socket = useContext(SocketContext);
  const cameraKeys = Object.keys(camerasStatus);

  const [state, setState] = useState({
    enabled: false,
    disabledCameras: [],
    timeoutSeconds: 10,
  });
  const [loading, setLoading] = useState(false);


  const socketCallbackHandler = (cb) => {
    if (cb.success && cb.memory) {
      setState((prev) => ({ ...prev, ...cb.memory }));
    }
  };

  const getState = useCallback(() => {
    setLoading(true);
    socket.emit('monitor candid photos', { action: 'getState' }, (response) => {
      socketCallbackHandler(response);
      setLoading(false);
    });
  }, [socket]);

  useEffect(() => {
    if (show) {
      getState();
    }
  }, [show]);


  const takePhoto = useCallback((camName) => {
    socket.emit('monitor candid photos', { action: 'takePhoto', camera: camName }, socketCallbackHandler);
  }, [socket]);

  const handleEnableToggle = useCallback((enabled) => {
    socket.emit('monitor candid photos', { action: 'setEnable', enabled }, socketCallbackHandler);
  }, [socket]);

  const handleCameraToggle = useCallback((camera, enabled) => {
    const newDisabledCameras = enabled
      ? state.disabledCameras.filter((cam) => cam !== camera)
      : [...state.disabledCameras, camera];

    socket.emit('monitor candid photos', {
      action: 'setDisabledCameras',
      cameras: newDisabledCameras,
    }, socketCallbackHandler);
  }, [socket, state.disabledCameras]);

  const handleTimeoutChange = useCallback((timeout) => {
    const timeoutValue = parseInt(timeout, 10);
    if (timeoutValue > 0) {
      socket.emit('monitor candid photos', { action: 'setTimeout', timeout: timeoutValue }, socketCallbackHandler);
    }
  }, [socket]);

  const isCameraDisabled = (camera) => state.disabledCameras.includes(camera);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="m-auto text-center">
          <h2 className="mt-4 mb-4">Candid Photos</h2>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCamera} className="mr-2" />
                <span>Enable</span>
              </div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="switcher mr-0">
                <input
                  type="checkbox"
                  checked={state.enabled}
                  className="switcher-input"
                  onChange={(e) => handleEnableToggle(e.target.checked)}
                  disabled={loading}
                />
                <span className="switcher-indicator">
                  <span className="switcher-yes" />
                  <span className="switcher-no" />
                </span>
              </label>
            </div>
          </Col>
        </Row>

        {/* Timeout Section */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faClock} className="mr-2" />
                <span>Timeout (seconds):</span>
              </div>
              <Form.Control
                type="number"
                min="1"
                max="60"
                value={state.timeoutSeconds}
                onChange={(e) => handleTimeoutChange(e.target.value)}
                style={{ width: '80px' }}
                disabled={loading}
              />
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            {cameraKeys.map((camName) => (
              <Row key={camName} className="mb-2">
                <Col>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faCamera} className="mr-2 pointer-events-none" onClick={() => takePhoto(camName)} />
                      <span>{camName}</span>
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="switcher mr-0">
                      <input
                        type="checkbox"
                        checked={!isCameraDisabled(camName)}
                        className="switcher-input"
                        onChange={(e) => handleCameraToggle(camName, e.target.checked)}
                        disabled={loading || !state.enabled}
                      />
                      <span className="switcher-indicator">
                        <span className="switcher-yes" />
                        <span className="switcher-no" />
                      </span>
                    </label>
                  </div>
                </Col>
              </Row>
            ))}
          </Col>
        </Row>

        <Row>
          <Col>
            <small className="text-muted">
              {state?.memory?.toString()}
            </small>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default connect(
  ({
    cameras: {
      status: camerasStatus,
    },
    events: {
      currentEvent: {
        slugDate,
      } = {},
    },
  }) => ({
    camerasStatus,
    slugDate,
  }),
)(CandidPhotosModal);
